import React from "react";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Provider as ReduxProvider } from "react-redux";

import { Toaster } from "../components/ui/sonner";

import { DataManagerGateway } from "../core/data-manager/gateway";
import { DataManagerUsecase } from "../core/data-manager/usecase";
import { DashboardGateway } from "../core/dashboard/gateway";
import { DashboardUsecase } from "../core/dashboard/usecase";
import { AccountGateway } from "../core/account/gateway";
import { AccountUsecase } from "../core/account/usecase";
import { SupportGateway } from "../core/support/gateway";
import { SupportUsecase } from "../core/support/usecase";
import { SharedGateway } from "../core/_shared/gateway";
import { SharedUsecase } from "../core/_shared/usecase";
import { GraphGateway } from "../core/graph/gateway";
import { GraphUsecase } from "../core/graph/usecase";
import { AuthGateway } from "../core/auth/gateway";
import { AuthUsecase } from "../core/auth/usecase";
import { AssistantGateway } from "../core/assistant/gateway";
import { AssistantUsecase } from "../core/assistant/usecase";
import { CateGateway } from "../core/cate/gateway";
import { CateUsecase } from "../core/cate/usecase";
import { CausalAiGateway } from "../core/causal-ai/gateway";
import { CausalAiUsecase } from "../core/causal-ai/usecase";
import { RcaGateway } from "../core/rca/gateway";
import { RcaUsecase } from "../core/rca/usecase";
import { AugmentationGateway } from "../core/augmentation/gateway";
import { AugmentationUsecase } from "../core/augmentation/usecase";
import { SimulationGateway } from "../core/simulation/gateway";
import { SimulationUsecase } from "../core/simulation/usecase";

import { DialogProvider, useError } from "../context/dialog";
import { useQueue } from "../context/queue/hooks";
import { AuthProvider } from "../context/auth";
import { ErrorModal } from "../components/error-modal";

import store from "../pages/graph/legacy/_store";

interface UseCases {
  dataManagerUsecase: DataManagerUsecase;
  dashboardUsecase: DashboardUsecase;
  accountUsecase: AccountUsecase;
  supportUsecase: SupportUsecase;
  sharedUsecase: SharedUsecase;
  graphUsecase: GraphUsecase;
  authUsecase: AuthUsecase;
  assistantUsecase: AssistantUsecase;
  cateUsecase: CateUsecase;
  causalAiUsecase: CausalAiUsecase;
  rcaUsecase: RcaUsecase;
  augmentationUsecase: AugmentationUsecase;
  simulationUsecase: SimulationUsecase;
}

const UsecaseContext = React.createContext<UseCases | null>(null);

export function UsecaseProvider({ children }: { children: React.ReactNode }) {
  const dataManagerRepo = new DataManagerGateway();
  const dashboardRepo = new DashboardGateway();
  const accountRepo = new AccountGateway();
  const supportRepo = new SupportGateway();
  const sharedRepo = new SharedGateway();
  const graphRepo = new GraphGateway();
  const authRepo = new AuthGateway();
  const assistantRepo = new AssistantGateway();
  const cateRepo = new CateGateway();
  const causalAiRepo = new CausalAiGateway();
  const rcaRepo = new RcaGateway();
  const augmentationRepo = new AugmentationGateway();
  const simulationRepo = new SimulationGateway();
  const { error } = useError();

  const { errorModal } = useQueue();

  const dataManagerUsecase = new DataManagerUsecase(dataManagerRepo, error);
  const dashboardUsecase = new DashboardUsecase(dashboardRepo, error);
  const accountUsecase = new AccountUsecase(accountRepo, error);
  const supportUsecase = new SupportUsecase(supportRepo, error);
  const sharedUsecase = new SharedUsecase(sharedRepo, error);
  const graphUsecase = new GraphUsecase(graphRepo, error);
  const authUsecase = new AuthUsecase(authRepo, error);
  const assistantUsecase = new AssistantUsecase(assistantRepo, error);
  const cateUsecase = new CateUsecase(cateRepo, error);
  const causalAiUsecase = new CausalAiUsecase(causalAiRepo, error);
  const rcaUsecase = new RcaUsecase(rcaRepo, error);
  const augmentationUsecase = new AugmentationUsecase(augmentationRepo, error);
  const simulationUsecase = new SimulationUsecase(simulationRepo, error);

  const usecases: UseCases = {
    dataManagerUsecase,
    dashboardUsecase,
    accountUsecase,
    supportUsecase,
    sharedUsecase,
    graphUsecase,
    authUsecase,
    assistantUsecase,
    cateUsecase,
    causalAiUsecase,
    rcaUsecase,
    augmentationUsecase,
    simulationUsecase,
  };

  return (
    <UsecaseContext.Provider value={usecases}>
      {children}
      <ErrorModal {...errorModal} />
    </UsecaseContext.Provider>
  );
}

export function Provider({ children }: { children: React.ReactNode }) {
  const queryClient = new QueryClient();

  return (
    <>
      <QueryClientProvider client={queryClient}>
        <DialogProvider>
          <UsecaseProvider>
            <AuthProvider>
              <ReduxProvider store={store}>{children}</ReduxProvider>
            </AuthProvider>
          </UsecaseProvider>
        </DialogProvider>
        <Toaster />
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </>
  );
}

export function useUsecases() {
  const context = React.useContext(UsecaseContext);
  if (!context) {
    throw new Error("useUsecases must be used within a Provider");
  }
  return context;
}
